import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Card,
    Chip,
    Button,
    Dialog,
    IconButton,
    Divider,
    Pagination,
    CircularProgress,
    Tooltip,
    Alert,
    Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import PageHeader from '../header/page-header';
import { getPurchaseOrder, getPoImages, uploadPoImages } from '../../api/goodsin.api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { LOCAL_STORAGE } from '../../constants/constants';
import { hasAccessLevel, itAccessIds, warehouseAccessIds } from '../../constants/access-ids';

const ITEMS_PER_PAGE = 12;

const STATUS_COLORS = {
    Received: 'success',
    Invoiced: 'info',
};

const PurchaseOrderDetails = () => {
    const navigate  = useNavigate();
    const { poId }  = useParams();
    const location  = useLocation();
    const poIdClean = decodeURIComponent(poId);

    const canUpload = useMemo(() => {
        if (location.state?.canUpload !== undefined) return location.state.canUpload;
        try {
            const token  = localStorage.getItem(LOCAL_STORAGE.JJ_INTERNAL);
            const groups = jwtDecode(token).groups ?? [];
            return hasAccessLevel(groups, itAccessIds) || hasAccessLevel(groups, warehouseAccessIds);
        } catch {
            return false;
        }
    }, [location.state]);

    const fileInputRef = useRef(null);

    const [po, setPo]             = useState(null);
    const [images, setImages]     = useState([]);
    const [loading, setLoading]   = useState(true);
    const [uploading, setUploading] = useState(false);

    const [page, setPage]               = useState(1);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => { fetchAll(); }, []);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [poData, imageData] = await Promise.all([
                getPurchaseOrder({ poId: poIdClean }),
                getPoImages({ poId: poIdClean })
            ]);
            setPo(poData);
            setImages(imageData?.images || []);
            setPage(1);
        } catch (err) {
            console.error('Failed to fetch PO details:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => navigate('/purchase-orders');

    /* ── Gallery pagination ── */
    const totalPages = useMemo(() => Math.max(1, Math.ceil(images.length / ITEMS_PER_PAGE)), [images.length]);

    const pagedImages = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return images.slice(start, start + ITEMS_PER_PAGE);
    }, [images, page]);

    /* ── Lightbox ── */
    const openLightbox  = (pageIndex) => setSelectedIndex((page - 1) * ITEMS_PER_PAGE + pageIndex);
    const closeLightbox = () => setSelectedIndex(null);
    const hasPrev = selectedIndex > 0;
    const hasNext = selectedIndex < images.length - 1;

    /* ── Upload ── */
    const handleUploadClick = () => fileInputRef.current?.click();

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setUploading(true);
        try {
            await uploadPoImages(poIdClean, files);
            setSnackbar({ open: true, message: `${files.length} file(s) uploaded successfully.`, severity: 'success' });
            await fetchAll();
        } catch (err) {
            console.error('Upload failed:', err);
            setSnackbar({ open: true, message: 'Upload failed. Please try again.', severity: 'error' });
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    /* ── PO detail fields — only render rows that have a value ── */
    const poFields = po ? [
        { label: 'PO ID',            value: po.poId            },
        { label: 'Date Received',    value: po.dateReceived    },
        { label: 'Supplier Account', value: po.supplierAccount },
        { label: 'Supplier Name',    value: po.supplierName    },
        { label: 'Branch',           value: po.branch          },
        { label: 'Received By',      value: po.receivedBy      },
    ].filter(({ value }) => value && value.toString().trim() !== '') : [];

    const hasStatus = po?.status && po.status.trim() !== '';

    if (loading) {
        return (
            <Container maxWidth="xl">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                    <CircularProgress sx={{ color: '#1e88e5' }} />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl">
            <PageHeader
                title={poIdClean}
                handleNavigation={handleBack}
                onRefresh={fetchAll}
                refreshNeeded={true}
            />

            {/* ── PO DETAILS CARD ── */}
            {po && (poFields.length > 0 || hasStatus) && (
                <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                            backgroundColor: 'rgb(33, 150, 243)',
                            color: 'white',
                            fontWeight: 'bold',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: '3px',
                            mb: 2,
                        }}
                    >
                        Purchase Order Details
                    </Typography>
                    <Grid container spacing={2}>
                        {poFields.map(({ label, value }) => (
                            <Grid item xs={12} sm={6} md={4} key={label}>
                                <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                                <Typography variant="body2" fontWeight="medium">{value}</Typography>
                            </Grid>
                        ))}
                        {hasStatus && (
                            <Grid item xs={12} sm={6} md={4}>
                                <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                                <Chip
                                    label={po.status}
                                    color={STATUS_COLORS[po.status] || 'default'}
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Paper>
            )}

            {/* ── GALLERY HEADER ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                    Goodsin Images
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({images.length} file{images.length !== 1 ? 's' : ''})
                    </Typography>
                </Typography>

                {canUpload && (
                    <Tooltip title="Upload images or scanned documents. Files will be named with the Goodsin convention and a -M suffix to indicate manual upload.">
                        <span>
                            <Button
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadFileIcon />}
                                onClick={handleUploadClick}
                                disabled={uploading || !po}
                                sx={{
                                    backgroundColor: 'white',
                                    color: 'rgb(38, 174, 93)',
                                    border: '2px solid rgb(38, 174, 93)',
                                    fontWeight: 'bold',
                                    borderRadius: '3px',
                                    boxShadow: 'rgba(0,0,0,0.3) 1px 1px',
                                    '&:hover': { backgroundColor: 'lightgreen' },
                                    '&.Mui-disabled': { opacity: 0.5 },
                                }}
                            >
                                {uploading ? 'Uploading…' : 'Upload Images / Scanned Docs'}
                            </Button>
                        </span>
                    </Tooltip>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFilesSelected}
                />
            </Box>

            {/* ── IMAGE GRID ── */}
            {images.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }} elevation={0} variant="outlined">
                    <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No attachments found for this PO.</Typography>
                </Paper>
            ) : (
                <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
                        {pagedImages.map((img, pageIndex) => {
                            const isManual = img.name?.includes('-M.');
                            return (
                                <Card
                                    key={`img-${pageIndex}`}
                                    sx={{
                                        cursor: 'pointer',
                                        border: isManual ? '2px solid #1565c0' : '1px solid #ccc',
                                        overflow: 'hidden',
                                        '&:hover': { boxShadow: 4 }
                                    }}
                                    onClick={() => openLightbox(pageIndex)}
                                >
                                    <Box
                                        component="img"
                                        src={img.url}
                                        alt={img.name}
                                        loading="lazy"
                                        sx={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                    <Box sx={{ p: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" noWrap sx={{ flex: 1 }}>
                                                {img.name}
                                            </Typography>
                                            {isManual && <Chip label="Manual" size="small" color="primary" />}
                                        </Box>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Box>

                    {images.length > ITEMS_PER_PAGE && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination
                                count={totalPages}
                                page={page}
                                onChange={(_, p) => setPage(p)}
                                color="primary"
                                siblingCount={0}
                                boundaryCount={1}
                            />
                        </Box>
                    )}
                </>
            )}

            {/* ── LIGHTBOX ── */}
            <Dialog
                open={selectedIndex !== null}
                onClose={closeLightbox}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        boxShadow: 'none',
                        // Constrain to viewport so the image + filename label are always visible without scrolling
                        maxWidth: '95vw',
                        maxHeight: '95vh',
                        overflow: 'hidden',
                    }
                }}
            >
                {selectedIndex !== null && images[selectedIndex] && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            position: 'relative',
                            // Reserve ~40px for filename label at bottom + padding
                            height: '95vh',
                            width: '95vw',
                            maxWidth: '95vw',
                            p: 2,
                            boxSizing: 'border-box',
                        }}
                    >
                        {/* Close button */}
                        <IconButton
                            onClick={closeLightbox}
                            sx={{
                                position: 'absolute', top: 8, right: 8,
                                color: 'white', backgroundColor: 'rgba(0,0,0,0.5)',
                                zIndex: 1,
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                        {/* Counter */}
                        <Typography
                            variant="caption"
                            sx={{
                                position: 'absolute', top: 16, left: '50%', transform: 'translateX(-50%)',
                                color: 'white', backgroundColor: 'rgba(0,0,0,0.5)',
                                px: 1.5, py: 0.5, borderRadius: 1, zIndex: 1,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {selectedIndex + 1} / {images.length}
                        </Typography>

                        {/* Prev button */}
                        <IconButton
                            disabled={!hasPrev}
                            onClick={() => setSelectedIndex(i => i - 1)}
                            sx={{
                                position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
                                color: 'white', backgroundColor: 'rgba(0,0,0,0.5)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                                '&.Mui-disabled': { opacity: 0.2 },
                                zIndex: 1,
                            }}
                        >
                            <ArrowBackIosNewIcon />
                        </IconButton>

                        {/* Image — fills available space between top chrome and filename label */}
                        <Box
                            component="img"
                            src={images[selectedIndex].url}
                            alt={images[selectedIndex].name}
                            sx={{
                                // flex: 1 lets the image fill remaining vertical space
                                flex: 1,
                                minHeight: 0,          // required for flex children to shrink below natural size
                                maxWidth: '100%',
                                objectFit: 'contain',
                                display: 'block',
                                mt: 5,                 // clear the counter / close button row
                                mb: 1,
                            }}
                        />

                        {/* Filename label — always visible at bottom */}
                        <Typography
                            variant="caption"
                            sx={{
                                flexShrink: 0,
                                textAlign: 'center',
                                color: 'white',
                                backgroundColor: 'rgba(0,0,0,0.55)',
                                px: 2, py: 0.5,
                                borderRadius: 1,
                                maxWidth: '80%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                mb: 0.5,
                            }}
                        >
                            {images[selectedIndex].name}
                        </Typography>

                        {/* Next button */}
                        <IconButton
                            disabled={!hasNext}
                            onClick={() => setSelectedIndex(i => i + 1)}
                            sx={{
                                position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)',
                                color: 'white', backgroundColor: 'rgba(0,0,0,0.5)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
                                '&.Mui-disabled': { opacity: 0.2 },
                                zIndex: 1,
                            }}
                        >
                            <ArrowForwardIosIcon />
                        </IconButton>
                    </Box>
                )}
            </Dialog>

            {/* ── SNACKBAR ── */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default PurchaseOrderDetails;